import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Button,
  Card,
  Check,
  Field,
  PageBody,
  PageHeader,
  Select,
  StatusPill,
  TextArea,
  TextInput,
  cx,
} from "../components/ui";
import { api, request } from "../lib/api";
import { useStore } from "../lib/store";
import type { SecretFlags, SecretPatch, SettingsPayload } from "../lib/types";

const EyeIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} {...props}>
    <path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6Z" />
    <circle cx="10" cy="10" r="2.5" />
  </svg>
);

const EyeOffIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.5} {...props}>
    <path d="m3 3 14 14" />
    <path d="M10 4c-1.6 0-3 .5-4.2 1.2" />
    <path d="M16 6.5C17.5 8 18 10 18 10s-1.5 3-3.6 4.7" />
    <path d="M9 16c.3 0 .7 0 1 0 5 0 8-6 8-6" />
  </svg>
);

interface SecretRowMeta {
  key: keyof SecretPatch;
  label: string;
  placeholder: string;
}

const SECRET_ROWS: SecretRowMeta[] = [
  { key: "esl_password", label: "ESL password", placeholder: "≥12 chars" },
  { key: "fish_api_key", label: "Fish Audio API key", placeholder: "Fish API key" },
  { key: "deepl_api_key", label: "DeepL API key", placeholder: "DeepL API key" },
  { key: "admin_password", label: "Admin password (rotates login)", placeholder: "≥8 chars" },
];

const TRUNK_ROWS: Array<{
  label: string;
  flag: keyof SecretFlags;
  busyKey: "bitcall" | "cd";
  userKey: keyof SecretPatch;
  passKey: keyof SecretPatch;
  proxyKey: keyof SecretPatch;
  rotated: string;
}> = [
  {
    label: "Bitcall trunk (Username / Password / Proxy)",
    flag: "has_bitcall",
    busyKey: "bitcall",
    userKey: "bitcall_username",
    passKey: "bitcall_password",
    proxyKey: "bitcall_proxy",
    rotated: "Bitcall trunk",
  },
  {
    label: "Voxio Telecom trunk (Username / Password / Proxy)",
    flag: "has_cd",
    busyKey: "cd",
    userKey: "cd_username",
    passKey: "cd_password",
    proxyKey: "cd_proxy",
    rotated: "Voxio Telecom trunk",
  },
];

function SecretField({
  id,
  value,
  onChange,
  busy,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  busy: boolean;
  placeholder: string;
}) {
  const [shown, setShown] = useState(false);
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
        New value
      </label>
      <div className="flex gap-2">
        <TextInput
          id={id}
          type={shown ? "text" : "password"}
          autoComplete="off"
          spellCheck={false}
          value={value}
          placeholder={placeholder}
          disabled={busy}
          onChange={(event) => onChange(event.target.value)}
          className="flex-1 font-mono"
        />
        <button
          type="button"
          aria-label={shown ? "Hide value" : "Show value"}
          aria-pressed={shown}
          disabled={busy}
          onClick={() => setShown((s) => !s)}
          className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] border border-border bg-inset text-ink-2 transition hover:bg-white/5 disabled:opacity-50"
        >
          {shown ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function TrunkTriple({
  rowKey,
  username,
  password,
  proxy,
  busy,
  showPwd,
  setValue,
}: {
  rowKey: string;
  username: string;
  password: string;
  proxy: string;
  busy: boolean;
  showPwd: boolean;
  setValue: (key: "username" | "password" | "proxy", next: string) => void;
}) {
  return (
    <>
      <Field label="Username">
        <TextInput
          id={`${rowKey}-username`}
          autoComplete="off"
          spellCheck={false}
          value={username}
          disabled={busy}
          onChange={(event) => setValue("username", event.target.value)}
          className="font-mono"
        />
      </Field>
      <Field label="Password">
        <TextInput
          id={`${rowKey}-password`}
          type={showPwd ? "text" : "password"}
          autoComplete="off"
          spellCheck={false}
          value={password}
          disabled={busy}
          onChange={(event) => setValue("password", event.target.value)}
          className="font-mono"
        />
      </Field>
      <Field label="Proxy">
        <TextInput
          id={`${rowKey}-proxy`}
          autoComplete="off"
          spellCheck={false}
          value={proxy}
          placeholder="host:port"
          disabled={busy}
          onChange={(event) => setValue("proxy", event.target.value)}
          className="font-mono"
        />
      </Field>
    </>
  );
}

/**
 * Operator settings. Secrets rotate via `POST /api/settings/secrets`;
 * operator copy + rotation + toggles save via `PATCH /api/settings`.
 */
export function Settings() {
  const { run, toast, logout } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    operator_name: "",
    business_name: "",
    default_purpose: "",
    rotation_pool: "",
    rotation_strategy: "round_robin" as NonNullable<SettingsPayload["rotation_strategy"]>,
    decision_timeout_sec: "120",
    identity_replay: true,
    avmd_enabled: true,
    recordings_enabled: true,
  });
  const [flags, setFlags] = useState({
    has_esl_password: false,
    has_fish_key: false,
    has_bitcall: false,
    has_cd: false,
    has_deepl: false,
    has_admin: false,
  });
  const [busy, setBusy] = useState(false);
  const [secretDraft, setSecretDraft] = useState<Partial<Record<keyof SecretPatch, string>>>({});
  const [secretBusy, setSecretBusy] = useState<string | null>(null);
  const [trunkPwdShown, setTrunkPwdShown] = useState<{ bitcall: boolean; cd: boolean }>({
    bitcall: false,
    cd: false,
  });
  const [adapters, setAdapters] = useState<AdapterFlag[]>(DEFAULT_ADAPTERS);

  useEffect(() => {
    void run(async () => {
      const data = await api.settings();
      setForm({
        operator_name: data.operator_name ?? "",
        business_name: data.business_name ?? "",
        default_purpose: data.default_purpose ?? "",
        rotation_pool: (data.rotation_pool ?? []).join("\n"),
        rotation_strategy: data.rotation_strategy ?? "round_robin",
        decision_timeout_sec: String(data.decision_timeout_sec ?? 120),
        identity_replay: data.identity_replay ?? true,
        avmd_enabled: data.avmd_enabled ?? true,
        recordings_enabled: data.recordings_enabled ?? true,
      });
      setFlags({
        has_esl_password: Boolean(data.has_esl_password),
        has_fish_key: Boolean(data.has_fish_key),
        has_bitcall: Boolean(data.has_bitcall),
        has_cd: Boolean(data.has_cd ?? false),
        has_deepl: Boolean(data.has_deepl),
        has_admin: Boolean(data.has_admin ?? data.has_esl_password ?? false),
      });
    }, "Could not load settings");
  }, [run]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await request<unknown>("/api/adapters");
        if (!cancelled) setAdapters(parseAdapters(data));
      } catch {
        if (!cancelled) setAdapters(DEFAULT_ADAPTERS);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const patch = (partial: Partial<typeof form>) => setForm((prev) => ({ ...prev, ...partial }));

  async function saveOperator() {
    const body: SettingsPayload = {
      operator_name: form.operator_name.trim(),
      business_name: form.business_name.trim(),
      default_purpose: form.default_purpose.trim(),
      rotation_pool: form.rotation_pool
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean),
      rotation_strategy: form.rotation_strategy,
      decision_timeout_sec: Number.parseInt(form.decision_timeout_sec, 10) || 120,
      identity_replay: form.identity_replay,
      avmd_enabled: form.avmd_enabled,
      recordings_enabled: form.recordings_enabled,
    };
    setBusy(true);
    const ok = await run(() => api.saveSettings(body), "Save settings failed");
    setBusy(false);
    if (ok) toast("Settings saved", "ok");
  }

  async function rotateSingle(row: SecretRowMeta, rotated: string) {
    const draft = (secretDraft[row.key] ?? "").trim();
    if (!draft) {
      toast("Enter a value first", "warn");
      return;
    }
    setSecretBusy(row.key);
    const ok = await run(async () => {
      const next = await api.saveSecrets({ [row.key]: draft } as SecretPatch);
      setFlags(next);
    }, "Rotate failed");
    setSecretBusy(null);
    if (!ok) return;
    setSecretDraft((prev) => ({ ...prev, [row.key]: "" }));
    toast(`${rotated} rotated`, "ok");
  }

  async function rotateAdmin(row: SecretRowMeta) {
    const draft = (secretDraft[row.key] ?? "").trim();
    if (draft.length < 8) {
      toast("Admin password must be at least 8 characters", "error");
      return;
    }
    setSecretBusy(row.key);
    const ok = await run(async () => {
      const next = await api.saveSecrets({ admin_password: draft });
      setFlags(next);
    }, "Rotate failed");
    setSecretBusy(null);
    if (!ok) return;
    setSecretDraft((prev) => ({ ...prev, admin_password: "" }));
    toast("Admin password rotated", "ok");
    await logout();
    navigate("/login");
  }

  async function rotateTrunk(meta: (typeof TRUNK_ROWS)[number]) {
    const user = (secretDraft[meta.userKey] ?? "").trim();
    const pass = (secretDraft[meta.passKey] ?? "").trim();
    const proxy = (secretDraft[meta.proxyKey] ?? "").trim();
    if (!user && !pass && !proxy) {
      toast("Enter at least one field", "warn");
      return;
    }
    setSecretBusy(meta.busyKey);
    const payload: SecretPatch = {};
    if (user) payload[meta.userKey] = user;
    if (pass) payload[meta.passKey] = pass;
    if (proxy) payload[meta.proxyKey] = proxy;
    const ok = await run(async () => {
      const next = await api.saveSecrets(payload);
      setFlags(next);
    }, "Rotate failed");
    setSecretBusy(null);
    if (!ok) return;
    setSecretDraft((prev) => ({
      ...prev,
      [meta.userKey]: "",
      [meta.passKey]: "",
      [meta.proxyKey]: "",
    }));
    toast(`${meta.rotated} rotated`, "ok");
  }

  const pillFor = (flag: keyof SecretFlags): "ok" | "fail" => (flags[flag] ? "ok" : "fail");

  return (
    <PageBody className="mx-auto flex w-full max-w-3xl flex-col">
      <PageHeader
        kicker="Tenant"
        title="Settings"
        actions={
          <>
            <Button variant="ghost" onClick={() => navigate("/sip")}>
              SIP trunks
            </Button>
            <Button variant="ghost" onClick={() => void logout()}>
              Sign out
            </Button>
          </>
        }
      />

      <Card className="p-5">
        <h2 className="mb-4 text-base font-medium text-ink">Tenant identity</h2>
        <div className="space-y-4">
          <Field label="Operator name">
            <TextInput
              value={form.operator_name}
              onChange={(event) => patch({ operator_name: event.target.value })}
              placeholder="Name they hear"
            />
          </Field>
          <Field label="Business">
            <TextInput
              value={form.business_name}
              onChange={(event) => patch({ business_name: event.target.value })}
              placeholder="Business name"
            />
          </Field>
          <Field label="Default purpose">
            <TextInput
              value={form.default_purpose}
              onChange={(event) => patch({ default_purpose: event.target.value })}
              placeholder="account verification"
            />
          </Field>
        </div>
      </Card>

      <Card className="mt-4 p-5">
        <h2 className="mb-4 text-base font-medium text-ink">Call behavior</h2>
        <div className="space-y-4">
          <Field label="CLI pool" hint="One owned E.164 per line. Empty falls back to +4474 random.">
            <TextArea
              value={form.rotation_pool}
              onChange={(event) => patch({ rotation_pool: event.target.value })}
              placeholder={"+447700900111\n+447700900222"}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Rotation">
              <Select
                value={form.rotation_strategy}
                onChange={(event) =>
                  patch({ rotation_strategy: event.target.value as typeof form.rotation_strategy })
                }
              >
                <option value="round_robin">round_robin</option>
                <option value="least_used">least_used</option>
                <option value="random">random</option>
              </Select>
            </Field>
            <Field label="Park decision timeout (sec)">
              <TextInput
                value={form.decision_timeout_sec}
                onChange={(event) => patch({ decision_timeout_sec: event.target.value })}
              />
            </Field>
          </div>
        </div>
      </Card>

      <Card className="mt-4 p-5">
        <h2 className="mb-4 text-base font-medium text-ink">Toggles</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <Check
            label="Identity replay"
            checked={form.identity_replay}
            onChange={(identity_replay) => patch({ identity_replay })}
          />
          <Check
            label="AVMD"
            checked={form.avmd_enabled}
            onChange={(avmd_enabled) => patch({ avmd_enabled })}
          />
          <Check
            label="Recordings"
            checked={form.recordings_enabled}
            onChange={(recordings_enabled) => patch({ recordings_enabled })}
          />
        </div>
      </Card>

      <Card className="mt-4 p-5">
        <h2 className="mb-1 text-base font-medium text-ink">Adapters</h2>
        <p className="mb-4 text-xs text-mute">Key presence only — values never shown.</p>
        <div className="grid gap-3 sm:grid-cols-3">
          {adapters.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between gap-3 rounded-[var(--radius-control)] border border-border bg-inset px-3 py-3"
            >
              <div className="text-sm text-ink">
                <span className="mr-1.5" aria-hidden>
                  {item.emoji}
                </span>
                {item.label}
              </div>
              <StatusPill tone={item.ready ? "ok" : "fail"}>
                {item.ready ? "Ready" : "Needs key"}
              </StatusPill>
            </div>
          ))}
        </div>
      </Card>

      <Card className="mt-4 p-5">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-base font-medium text-ink">Secrets</h2>
          <span className="font-mono text-[11px] tracking-[0.16em] text-mute">
            one save per row · values never echo
          </span>
        </div>
        <p className="mb-4 text-sm text-mute">
          Rotate ESL / Fish / DeepL / login here. Trunk apply, active gateway, and REGED live on{" "}
          <button type="button" className="text-teal underline-offset-2 hover:underline" onClick={() => navigate("/sip")}>
            SIP
          </button>
          .
        </p>

        {SECRET_ROWS.map((row, index) => {
          const isAdmin = row.key === "admin_password";
          const pillTone: "mute" | "ok" | "fail" = isAdmin
            ? "mute"
            : pillFor(FLAG_TO[row.key] as keyof SecretFlags);
          const pillText = isAdmin
            ? "always set"
            : flags[FLAG_TO[row.key] as keyof SecretFlags]
              ? "set"
              : "missing";
          const busy = secretBusy === row.key;
          return (
            <div
              key={row.key}
              className={cx(
                "border-t border-border py-4",
                index === 0 && "border-t-0 pt-4",
              )}
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
                  {row.label}
                </div>
                <StatusPill tone={pillTone}>{pillText}</StatusPill>
              </div>
              <SecretField
                id={row.key}
                value={secretDraft[row.key] ?? ""}
                onChange={(next) =>
                  setSecretDraft((prev) => ({ ...prev, [row.key]: next }))
                }
                busy={busy}
                placeholder={row.placeholder}
              />
              <div className="mt-3 flex justify-end">
                <Button
                  variant="subtle"
                  disabled={busy}
                  onClick={() =>
                    isAdmin ? void rotateAdmin(row) : void rotateSingle(row, row.label.split(" ")[0])
                  }
                >
                  {busy ? "Saving…" : isAdmin ? "Rotate + sign out" : "Save"}
                </Button>
              </div>
            </div>
          );
        })}

        {TRUNK_ROWS.map((meta, index) => {
          const busy = secretBusy === meta.busyKey;
          const pillTone: "ok" | "fail" = pillFor(meta.flag);
          return (
            <div
              key={meta.busyKey}
              className={cx(
                "border-t border-border py-4",
                index === 0 && "border-t-0 pt-4",
              )}
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
                  {meta.label}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      setTrunkPwdShown((prev) => ({ ...prev, [meta.busyKey]: !prev[meta.busyKey] }))
                    }
                    className="inline-flex items-center gap-1 font-mono text-[11px] text-mute hover:text-ink disabled:opacity-50"
                  >
                    {trunkPwdShown[meta.busyKey] ? (
                      <EyeOffIcon className="h-3.5 w-3.5" />
                    ) : (
                      <EyeIcon className="h-3.5 w-3.5" />
                    )}
                    {trunkPwdShown[meta.busyKey] ? "hide password" : "show password"}
                  </button>
                  <StatusPill tone={pillTone}>{flags[meta.flag] ? "set" : "missing"}</StatusPill>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <TrunkTriple
                  rowKey={meta.busyKey}
                  username={secretDraft[meta.userKey] ?? ""}
                  password={secretDraft[meta.passKey] ?? ""}
                  proxy={secretDraft[meta.proxyKey] ?? ""}
                  busy={busy}
                  showPwd={trunkPwdShown[meta.busyKey]}
                  setValue={(key, next) => {
                    const draftKey =
                      key === "username" ? meta.userKey : key === "password" ? meta.passKey : meta.proxyKey;
                    setSecretDraft((prev) => ({ ...prev, [draftKey]: next }));
                  }}
                />
              </div>
               <div className="mt-3 flex justify-end">
                <Button variant="subtle" disabled={busy} onClick={() => void rotateTrunk(meta)}>
                  {busy ? "Saving…" : "Save trunk"}
                </Button>
              </div>
            </div>
          );
        })}

        <div className="border-t border-border py-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-[11px] font-medium uppercase tracking-[0.16em] text-mute">
              Voxa SIP trunk (IP whitelist / peer)
            </div>
            <StatusPill tone="ok">no password</StatusPill>
          </div>
          <p className="text-sm text-mute">
            Sofia id <span className="font-mono text-ink">voxa</span> · proxy{" "}
            <span className="font-mono text-ink">38.60.176.238:5060</span> UDP. Peer
            auth — this host is already whitelisted. Select <span className="font-mono">voxa</span>{" "}
            on New call or SIP accounts. Does not replace Voxio Telecom{" "}
            <span className="font-mono">cd</span>{" "}
            or Bitcall.
          </p>
        </div>
      </Card>

      <div className="mt-6 flex justify-between">
        <Button variant="danger" onClick={() => void logout()}>
          Sign out
        </Button>
        <Button variant="primary" disabled={busy} onClick={() => void saveOperator()}>
          Save settings
        </Button>
      </div>
    </PageBody>
  );
}

interface AdapterFlag {
  id: string;
  label: string;
  emoji: string;
  ready: boolean;
}

const ADAPTER_META: Array<{ id: string; label: string; emoji: string; keys: string[] }> = [
  { id: "fish", label: "Fish Audio", emoji: "🐟", keys: ["fish", "fish_audio", "tts"] },
  { id: "deepl", label: "DeepL", emoji: "🌐", keys: ["deepl", "translation"] },
  { id: "sms", label: "SMS", emoji: "💬", keys: ["sms"] },
];

function asAdapterRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function adapterReady(value: unknown): boolean {
  if (value === true || value === 1 || value === "true") return true;
  if (value === false || value === 0 || value === "false" || value == null) return false;
  const row = asAdapterRecord(value);
  if (row.needs_key === true) return false;
  return row.has_key === true || row.enabled === true || row.ready === true;
}

function parseAdapters(raw: unknown): AdapterFlag[] {
  const record = asAdapterRecord(raw);
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(record.adapters)
      ? record.adapters
      : null;
  const byId = new Map<string, unknown>();
  if (Array.isArray(list)) {
    for (const item of list) {
      const row = asAdapterRecord(item);
      const id = String(row.id ?? row.slug ?? "").trim().toLowerCase();
      if (id) byId.set(id, item);
    }
  } else {
    for (const [key, value] of Object.entries(record)) {
      byId.set(key.toLowerCase(), value);
    }
  }
  return ADAPTER_META.map((meta) => {
    const value = meta.keys.map((key) => byId.get(key)).find((item) => item !== undefined);
    return {
      id: meta.id,
      label: meta.label,
      emoji: meta.emoji,
      ready: adapterReady(value),
    };
  });
}

const DEFAULT_ADAPTERS: AdapterFlag[] = ADAPTER_META.map((meta) => ({
  id: meta.id,
  label: meta.label,
  emoji: meta.emoji,
  ready: false,
}));

const FLAG_TO: Partial<Record<keyof SecretPatch, keyof SecretFlags>> = {
  esl_password: "has_esl_password",
  fish_api_key: "has_fish_key",
  deepl_api_key: "has_deepl",
  admin_password: "has_admin",
};